import "@browsemate/design-tokens/tokens.css";
import {
  HttpIdentityTransport,
  receiverSecret,
  receiverChallenge,
  startCenterLogin,
  centerHandoff,
  HttpSessionTransport,
  SessionController,
  createAuthClientConfiguration,
  type SessionStorage,
  type StoredSession,
} from "@browsemate/auth-client";
import React from "react";
import {
  browserSessionClient,
  withBrowserSessionLock,
} from "./browser-session";
import { AuthPage } from "@wushushuochanpin/identity-react/page";
import "@wushushuochanpin/identity-react/tokens.css";
import "@wushushuochanpin/identity-react/login.css";
import "./styles.css";

const oauthStorageKey = "browsemate.oauth.pending";
const sessionStorageKey = "browsemate.session";
const configuration = createAuthClientConfiguration(
  new URL(globalThis.location.origin),
);
const identityTransport = new HttpIdentityTransport(configuration);

type PageState =
  | { phase: "loading" }
  | { phase: "ready"; wechat: boolean; center: boolean }
  | { phase: "redirecting" }
  | { phase: "callback" }
  | { phase: "authenticated"; target: "extension" | "web" }
  | { phase: "error"; message: string };

interface PendingOAuth {
  readonly state: string;
  readonly nonce: string;
  readonly pkceVerifier: string;
  readonly expiresAt: string;
  readonly target: "extension" | "web";
}

export function LegacyAccountShell() {
  const [state, setState] = React.useState<PageState>({ phase: "loading" });

  const started = React.useRef(false);
  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    const active = true;
    const run = async () => {
      const query = new URLSearchParams(location.search);
      if (query.get("center") === "error") {
        setState({ phase: "error", message: "登录未完成，请重新发起。" });
        return;
      }
      if (query.get("center") === "complete") {
        setState({ phase: "callback" });
        try {
          const code = await centerHandoff(configuration.apiOrigin);
          if (query.get("target") === "extension") {
            if (
              !(await deliverToExtension(
                code,
                true,
                sessionStorage.getItem("browsemate.center.challenge") ??
                  undefined,
              ))
            )
              throw new Error("EXTENSION_HANDOFF_PENDING");
            setState({ phase: "authenticated", target: "extension" });
          } else {
            const verifier = sessionStorage.getItem(
              "browsemate.center.receiver",
            );
            if (!verifier) throw new Error("MISSING_RECEIVER");
            await establishWebSession(code, verifier);
            sessionStorage.removeItem("browsemate.center.receiver");
            setState({ phase: "authenticated", target: "web" });
          }
          sessionStorage.removeItem("browsemate.center.challenge");
          if (query.get("target") === "extension")
            history.replaceState({}, "", "/");
          else location.replace("/");
        } catch {
          setState({
            phase: "error",
            message: "登录交接未完成。请确认扩展已安装并启用，再重新发起登录。",
          });
        }
        return;
      }
      if (location.pathname === "/auth/wechat/callback") {
        if (active) setState({ phase: "callback" });
        await completeWeChatCallback(active, setState);
        return;
      }
      try {
        if (sessionStorage.getItem(sessionStorageKey)) {
          const restored = await webSession().restore();
          if (restored) {
            setState({ phase: "authenticated", target: "web" });
            return;
          }
        }
        const providers = await identityTransport.providers();
        if (active)
          setState({
            phase: "ready",
            wechat: providers.includes("wechat"),
            center: providers.includes("center"),
          });
      } catch {
        if (active)
          setState({
            phase: "error",
            message: "暂时无法连接登录服务，请稍后重试。",
          });
      }
    };
    void run();
    return () => {};
  }, []);

  const startWeChat = async () => {
    setState({ phase: "redirecting" });
    try {
      if (state.phase === "ready" && state.center) {
        const query = new URLSearchParams(location.search);
        const target =
          query.get("target") === "extension" ? "extension" : "web";
        let challenge = query.get("receiver_challenge") ?? "";
        if (target === "web") {
          const verifier = receiverSecret();
          sessionStorage.setItem("browsemate.center.receiver", verifier);
          challenge = await receiverChallenge(verifier);
        }
        if (!/^[A-Za-z0-9_-]{43}$/.test(challenge))
          throw new Error("INVALID_RECEIVER");
        location.assign(
          await startCenterLogin(configuration.apiOrigin, target, challenge),
        );
        return;
      }
      const transaction = await identityTransport.start("wechat");
      const pending: PendingOAuth = {
        state: transaction.state,
        nonce: transaction.nonce,
        pkceVerifier: transaction.pkceVerifier,
        expiresAt: transaction.expiresAt,
        target:
          new URLSearchParams(location.search).get("target") === "extension"
            ? "extension"
            : "web",
      };
      sessionStorage.setItem(oauthStorageKey, JSON.stringify(pending));
      location.assign(transaction.authorizationUrl);
    } catch {
      setState({ phase: "error", message: "微信登录启动失败，请重试。" });
    }
  };

  const message =
    state.phase === "error"
      ? state.message
      : state.phase === "loading"
        ? "正在检查可用登录方式…"
        : state.phase === "redirecting"
          ? "正在前往安全登录…"
          : state.phase === "callback"
            ? "正在完成登录，请不要关闭页面…"
            : state.phase === "authenticated"
              ? state.target === "extension"
                ? "会话已交给网海轻舟扩展，可以关闭此页面。"
                : "账户会话已建立。密码库仍需单独解锁。"
              : state.wechat || state.center
                ? "通过账号中心登录，支持已启用的微信扫码。"
                : "微信登录正在配置中，请稍后再试。";
  return (
    <AuthPage
      productName="BrowseMate"
      brandName="网海轻舟"
      title={state.phase === "authenticated" ? "登录成功" : "账号登录"}
      tone={
        state.phase === "error"
          ? "error"
          : state.phase === "authenticated"
            ? "success"
            : "normal"
      }
      message={message}
      footer="登录只验证身份，不会解锁或上传密码库。"
      actions={
        <>
          {state.phase === "ready" && (state.wechat || state.center) && (
            <button type="button" onClick={() => void startWeChat()}>
              登录账号中心
            </button>
          )}
          {state.phase === "error" && (
            <button type="button" onClick={() => location.assign("/")}>
              返回登录
            </button>
          )}
          {state.phase === "authenticated" && state.target === "web" && (
            <button
              type="button"
              className="idc-login__secondary"
              onClick={() => {
                void webSession()
                  .logout()
                  .then(
                    () => location.assign("/"),
                    () =>
                      setState({
                        phase: "error",
                        message: "本地会话已清理，服务端退出结果待确认。",
                      }),
                  );
              }}
            >
              退出本设备
            </button>
          )}
        </>
      }
    />
  );
}

async function completeWeChatCallback(
  active: boolean,
  setState: React.Dispatch<React.SetStateAction<PageState>>,
) {
  const query = new URLSearchParams(location.search);
  const code = query.get("code");
  const returnedState = query.get("state");
  const pending = readPendingOAuth();
  history.replaceState({}, "", location.pathname);
  if (
    !code ||
    !returnedState ||
    !pending ||
    returnedState !== pending.state ||
    Date.parse(pending.expiresAt) <= Date.now()
  ) {
    sessionStorage.removeItem(oauthStorageKey);
    if (active)
      setState({
        phase: "error",
        message: "登录请求已失效，请返回后重新扫码。",
      });
    return;
  }
  try {
    const result = await identityTransport.callback("wechat", {
      state: pending.state,
      nonce: pending.nonce,
      pkceVerifier: pending.pkceVerifier,
      code,
    });
    sessionStorage.removeItem(oauthStorageKey);
    if (!result.exchangeCode) {
      throw new Error("missing exchange code");
    }
    const delivered =
      pending.target === "extension"
        ? await deliverToExtension(result.exchangeCode)
        : false;
    if (pending.target === "extension" && !delivered)
      throw new Error("EXTENSION_HANDOFF_PENDING");
    if (pending.target === "web")
      await establishWebSession(result.exchangeCode);
    if (active)
      setState({
        phase: "authenticated",
        target: pending.target,
      });
  } catch {
    if (active)
      setState({
        phase: "error",
        message:
          pending.target === "extension"
            ? "登录已完成，但未能交给扩展。请确认扩展已启用，然后从扩展入口重新发起登录。"
            : "微信登录未完成，请返回后重新扫码。",
      });
  }
}

function readPendingOAuth(): PendingOAuth | undefined {
  try {
    const value: unknown = JSON.parse(
      sessionStorage.getItem(oauthStorageKey) ?? "null",
    );
    if (typeof value !== "object" || value === null) return undefined;
    const pending = value as Partial<PendingOAuth>;
    if (
      typeof pending.state !== "string" ||
      typeof pending.nonce !== "string" ||
      typeof pending.pkceVerifier !== "string" ||
      typeof pending.expiresAt !== "string" ||
      (pending.target !== "extension" && pending.target !== "web")
    )
      return undefined;
    return pending as PendingOAuth;
  } catch {
    return undefined;
  }
}

export async function deliverToExtension(
  exchangeCode: string,
  center = false,
  receiverChallenge?: string,
): Promise<boolean> {
  const requestId = crypto.randomUUID();
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener("message", listener);
      resolve(false);
    }, 10000);
    const listener = (event: MessageEvent) => {
      if (
        event.source !== window ||
        event.origin !== location.origin ||
        typeof event.data !== "object" ||
        event.data === null ||
        event.data.source !== "browsemate-extension" ||
        event.data.requestId !== requestId
      )
        return;
      window.clearTimeout(timeout);
      window.removeEventListener("message", listener);
      resolve(event.data.ok === true);
    };
    window.addEventListener("message", listener);
    window.postMessage(
      {
        source: "browsemate-account-web",
        type: center
          ? "BROWSEMATE_CENTER_EXCHANGE"
          : "BROWSEMATE_SESSION_EXCHANGE",
        requestId,
        exchangeCode,
        ...(receiverChallenge ? { receiverChallenge } : {}),
      },
      location.origin,
    );
  });
}

let accountSession: SessionController | undefined;
export function forgetLegacyWebSession() {
  sessionStorage.removeItem(sessionStorageKey);
  accountSession = undefined;
}
export function webSession(): SessionController {
  if (accountSession) return accountSession;
  const storage: SessionStorage = {
    async load() {
      const raw = sessionStorage.getItem(sessionStorageKey);
      return raw ? (JSON.parse(raw) as StoredSession) : undefined;
    },
    async save(session) {
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(session));
    },
    async clear() {
      sessionStorage.removeItem(sessionStorageKey);
    },
  };
  accountSession = new SessionController(
    storage,
    new HttpSessionTransport(configuration),
  );
  return accountSession;
}
export async function establishWebSession(
  exchangeCode: string,
  receiverVerifier?: string,
) {
  if (receiverVerifier) {
    await withBrowserSessionLock(() =>
      browserSessionClient().exchange(exchangeCode, receiverVerifier),
    );
    forgetLegacyWebSession();
    return;
  }
  await webSession().exchange({
    exchangeCode,
    ...(receiverVerifier ? { receiverVerifier } : {}),
    displayName: "账户网页",
    platform: "consumer-web",
    clientVersion: "0.1.0",
  });
}
